import { Box, Button, ButtonProps, Dialog, Typography } from '@mui/material';
import React, { ReactNode, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogBoxContent } from '../DialogBoxContent/DialogBoxContent';
import { Loader } from '../Loader/Loader';

interface ConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    text: string | ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    onConfirm: () => void;
    isLoading?: boolean;
    confirmColor?: ButtonProps['color'];
    extraContent?: ReactNode;
    testId?: string;
    confirmButtonTestClass?: string;
}

export const ConfirmDialog = memo(
    ({
        open,
        onClose,
        title,
        text,
        confirmLabel,
        cancelLabel,
        onConfirm,
        isLoading = false,
        confirmColor,
        extraContent,
        testId,
        confirmButtonTestClass,
    }: ConfirmDialogProps) => {
        const { t } = useTranslation();

        const handleClose = () => {
            if (!isLoading) {
                onClose();
            }
        };

        return (
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={
                    {
                        'data-testid': testId,
                    } as React.HTMLAttributes<HTMLDivElement>
                }
            >
                <DialogBoxContent
                    onClose={handleClose}
                    buttons={
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            width="100%"
                            gap="10px"
                        >
                            <Button disabled={isLoading} onClick={handleClose}>
                                {cancelLabel ?? t('Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                color={confirmColor}
                                data-testclass={confirmButtonTestClass}
                                startIcon={
                                    isLoading && <Loader color="inherit" />
                                }
                                disabled={isLoading}
                                onClick={onConfirm}
                            >
                                {confirmLabel}
                            </Button>
                        </Box>
                    }
                >
                    <Box maxWidth="400px">
                        <Typography mb="20px" typography="poppins.subtitle2">
                            {title}
                        </Typography>
                        <Typography mb="8px" typography="openSans.body2">
                            {text}
                        </Typography>
                        {extraContent}
                    </Box>
                </DialogBoxContent>
            </Dialog>
        );
    },
);
